import { useContext, useId } from "react";

import { Grid, Stack } from "@mui/material";
import { FormType } from "@simplyk/common";
import { useWatch } from "react-hook-form";

import { FormSelectField } from "../../../../../components/forms-design-system/FormSelectField/FormSelectField";
import { FormSelectItem } from "../../../../../components/forms-design-system/FormSelectField/FormSelectItem";
import { FrontendFormContext } from "../../../../../contexts/FrontendFormContext";
import { useFormV2Context } from "../../../context/FormV2Context";

import { AmplitudeEvents } from "@/constants/amplitude";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormTextField } from "@/forms-design-system/FormTextField";
import { isEmail, isRequired } from "@/helpers/validators";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/hooks/useTranslate";

export const InHonour = () => {
  const { t } = useTranslate();
  const { logAmplitudeEventCallback } = useAmplitude();
  const { isEmbed } = useContext(FrontendFormContext);
  const { formObject, form, formCountry } = useFormV2Context(FormType.DonationForm);
  const { errors } = form.formState;

  const notifyEmailCheckboxId = useId();
  const inHonourNameId = useId();
  const inHonourNotifyEmailId = useId();
  const inHonourMessageId = useId();

  const isInHonour = useWatch({ control: form.control, name: "inHonourOf" });
  const notifySomeone = useWatch({ control: form.control, name: "notifySomeone" });

  const amplitudePayload = {
    isEmbed,
    formCategory: formObject.category,
    formId: formObject.id,
    organizationCountry: formCountry,
  };

  return (
    <Stack gap={2}>
      {/* Main checkbox to enable donation in honor of someone */}
      <FormCheckbox
        control={form.control}
        name="inHonourOf"
        id={useId()}
        label={t("donationForm", "donationInHonourOrMemoryOfSomeone")}
        data-test="donation-form-in-honour-of"
      />

      {isInHonour && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormSelectField
                control={form.control}
                name="tribute"
                required
                defaultValue="inHonourOf"
                renderValue={(value) => {
                  if (value === "inHonourOf") {
                    return t("donationForm", "donationInHonorOf");
                  }
                  if (value === "inMemoryOf") {
                    return t("donationForm", "donationInMemoryOf");
                  }
                  return value;
                }}
              >
                <FormSelectItem value="inHonourOf">{t("donationForm", "donationInHonorOf")}</FormSelectItem>
                <FormSelectItem value="inMemoryOf">{t("donationForm", "donationInMemoryOf")}</FormSelectItem>
              </FormSelectField>
            </Grid>

            {/* Field for the honoree's name */}
            <Grid item xs={12} md={6}>
              <FormTextField
                control={form.control}
                name="inHonourName"
                rules={[isRequired(isInHonour, t("donationForm", "inHonourNameRequired"))]}
                id={inHonourNameId}
                error={!!errors.inHonourName?.message}
                helperText={errors.inHonourName?.message}
                onFocus={logAmplitudeEventCallback(AmplitudeEvents.DonationFocusGeneralInput, {
                  label: "inHonourName",
                  ...amplitudePayload,
                })}
                autoFocus
                required
              />
            </Grid>
          </Grid>

          {/* Checkbox to enable notification functionality */}
          <FormCheckbox
            control={form.control}
            name="notifySomeone"
            id={notifyEmailCheckboxId}
            label={t("donationForm", "notifySomeoneForTheDonation")}
          />

          {notifySomeone && (
            <>
              {/* Email field for notification recipient */}
              <FormTextField
                control={form.control}
                name="inHonourNotifyEmail"
                rules={[
                  isRequired(notifySomeone, t("donationForm", "inHonourNotifyEmailRequired")),
                  isEmail(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
                ]}
                id={inHonourNotifyEmailId}
                label={t("donationForm", "inHonourNotifyEmailLabel")}
                error={!!errors.inHonourNotifyEmail?.message}
                helperText={errors.inHonourNotifyEmail?.message}
                onFocus={logAmplitudeEventCallback(AmplitudeEvents.DonationFocusGeneralInput, {
                  label: "inHonourNotifyEmail",
                  ...amplitudePayload,
                })}
                autoFocus
                required
              />

              {/* Message field for custom notification message */}
              <FormTextField
                control={form.control}
                name="inHonourMessage"
                rules={[isRequired(notifySomeone, t("donationForm", "inHonourMessageRequired"))]}
                id={inHonourMessageId}
                multiline
                rows={4}
                label={t("donationForm", "inHonourMessageLabel")}
                error={!!errors.inHonourMessage?.message}
                helperText={errors.inHonourMessage?.message}
                onFocus={logAmplitudeEventCallback(AmplitudeEvents.DonationFocusGeneralInput, {
                  label: "inHonourMessage",
                  ...amplitudePayload,
                })}
                required
              />
            </>
          )}
        </>
      )}
    </Stack>
  );
};
